import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fun, makeTemplate, getView } from "./utils";
import dayjs from "dayjs";

import { useGridContext } from "./reducer";
import { FormControl } from "@/comn/components";
import { comnUtils } from "@/comn/utils";
import { useModal } from "@/comn/hooks";
import React from "react";

export const GridCompare = () => {
    const { _grid, headSchema, bodySchema } = useGridContext();

    const [compare, setCompare] = useState(false);
    const { t } = useTranslation();
    const modal = useModal();
    const regex = RegExp(/^\d{2}\/\d{2}\/\d{4}$/);

    _grid.current._openCompare = (arg?: any) => {
        _grid.current._gridColGroup = makeTemplate(headSchema, "table").split(" ");
        _grid.current._compareRows = arg;

        //console.log(_grid.current._compareRows);
        setCompare(true);
    };

    const tableRef = useCallback((ref: any) => {
        if (!ref) return;

        ref.querySelectorAll("select").forEach((p: any) => {
            p.replaceWith(p.value === "" ? "" : p.options[p.selectedIndex].text);
        });

        const before = ref.querySelectorAll("tbody tr[data-row='0']");
        const after = ref.querySelectorAll("tbody tr[data-row='1']");

        for (let i = 0; i < before.length; i++) {
            before[i].querySelectorAll("td").forEach((td: any, index: number) => {
                let beforeValue = td.outerText;
                let afterValue = after[i].querySelectorAll("td")[index].outerText;

                if (beforeValue !== afterValue && index > 0) {
                    after[i].querySelectorAll("td")[index].className += " bg-warning";
                }

                td.replaceChildren(beforeValue);
                after[i].querySelectorAll("td")[index].replaceChildren(afterValue);
            });
        }

        modal.openModal({
            content: <div dangerouslySetInnerHTML={{ __html: ref.outerHTML }}></div>,
            size: "lg",
            draggable: true,
        });
        setCompare(false);
    }, []);

    if (!compare) return null;

    return (
        <div className={"overflow-x-auto"}>
            <table ref={tableRef} className="w-max min-w-full">
                <colgroup>
                    <col width="100" key={999} />
                    {_grid.current._gridColGroup.map((col: any, index: number) => {
                        return <col width={col} key={index} />;
                    })}
                </colgroup>
                <thead className="[&_th]:border">
                    {headSchema.map((cols: any, rowIndex: any) => {
                        return (
                            <tr key={_grid.current._key + ".thr." + rowIndex}>
                                {cols.map((cel: any, celIndex: any) => {
                                    if (!cel) return null;

                                    if (rowIndex === 0 && celIndex === 0) {
                                        return (
                                            <>
                                                <th
                                                    key={"th"}
                                                    rowSpan={headSchema.length}
                                                    colSpan={1}
                                                    className={
                                                        "p-1 py-2 bg-header min-h-[2.5rem] text-center font-semibold"
                                                    }
                                                >
                                                    {t("L_TP")}
                                                </th>
                                                <th
                                                    key={_grid.current._key + ".th." + rowIndex + "." + celIndex}
                                                    rowSpan={cel.rowspan}
                                                    colSpan={cel.colspan}
                                                    className={
                                                        "p-1 py-2 bg-header min-h-[2.5rem] text-center font-semibold"
                                                    }
                                                >
                                                    {t(cel.header)}
                                                </th>
                                            </>
                                        );
                                    } else {
                                        return (
                                            <th
                                                key={_grid.current._key + ".th." + rowIndex + "." + celIndex}
                                                rowSpan={cel.rowspan}
                                                colSpan={cel.colspan}
                                                className={
                                                    "p-1 py-2 bg-header min-h-[2.5rem] text-center font-semibold"
                                                }
                                            >
                                                {t(cel.header)}
                                            </th>
                                        );
                                    }
                                })}
                            </tr>
                        );
                    })}
                </thead>
                <tbody>
                    {_grid.current._compareRows.map((row: any, rowIndex: any) => {
                        return bodySchema.map((cols: any, colIndex: any) => {
                            return (
                                <tr key={_grid.current._key + ".tbr." + rowIndex + "." + colIndex} data-row={rowIndex}>
                                    {cols.map((cel: any, celIndex: any) => {
                                        if (!cel) return null;

                                        const { binding, align, rowspan, colspan, edit, header, type, ...FORM } = cel;

                                        const BINDING_VAL = row[binding];
                                        const FORMATTED_VAL = comnUtils.getFormattedValue(BINDING_VAL, FORM);
                                        FORM.type = type;

                                        const CELL_CONTEXT = {
                                            binding,
                                            rowValues: row,
                                            value: BINDING_VAL,
                                            formattedValue: FORMATTED_VAL,
                                        };

                                        const BG_COLORS = {
                                            blue: "bg-[#bacee0]",
                                            yellow: "bg-[#ffeb33]",
                                            red: "bg-[#ed3e49]",
                                        };

                                        const TEXT_COLORS = {
                                            blue: "text-[#bacee0]",
                                            yellow: "text-[#ffeb33]",
                                            red: "text-[#ed3e49]",
                                        };

                                        switch (FORM.type) {
                                            case "daterange":
                                            case "timerange":
                                                {
                                                    const START_VAL = row[FORM.start.binding];
                                                    const END_VAL = row[FORM.end.binding];
                                                    FORM.start.type = FORM.type === "daterange" ? "date" : "time";
                                                    FORM.end.type = FORM.type === "daterange" ? "date" : "time";
                                                    FORM.start.value = comnUtils.getFormattedValue(
                                                        START_VAL,
                                                        FORM.start,
                                                    );
                                                    FORM.end.value = comnUtils.getFormattedValue(END_VAL, FORM.end);
                                                }
                                                break;
                                            default:
                                                {
                                                    FORM.value = FORMATTED_VAL;
                                                }
                                                break;
                                        }
                                        FORM.edit = false;
                                        const Control = <FormControl {...FORM} />;

                                        let cellContext: any = {};
                                        const CustomCell = _grid.current._render?.[edit ? "edit" : "cell"]?.[binding]?.(
                                            { ...CELL_CONTEXT, control: Control },
                                            cellContext,
                                        );

                                        const content = type === "select" ? BINDING_VAL : CustomCell || Control;

                                        return (
                                            <>
                                                {celIndex === 0 && rowIndex === 0 && (
                                                    <td
                                                        className={"p-1 py-2 min-h-[2.5rem] border text-center bg-card"}
                                                        key={_grid.current._key + ".td." + rowIndex}
                                                        rowSpan={cel.rowspan}
                                                        colSpan={cel.colspan}
                                                    >
                                                        Before
                                                    </td>
                                                )}
                                                {celIndex === 0 && rowIndex === 1 && (
                                                    <td
                                                        className={"p-1 py-2 min-h-[2.5rem] border text-center bg-card"}
                                                        key={_grid.current._key + ".td." + rowIndex}
                                                        rowSpan={cel.rowspan}
                                                        colSpan={cel.colspan}
                                                    >
                                                        After
                                                    </td>
                                                )}

                                                <td
                                                    className={
                                                        "p-1 py-2 min-h-[2.5rem] border text-center bg-card text-" +
                                                        cel.align
                                                    }
                                                    key={_grid.current._key + ".td." + rowIndex + "." + celIndex}
                                                    rowSpan={cel.rowspan}
                                                    colSpan={cel.colspan}
                                                >
                                                    {content}
                                                </td>
                                            </>
                                        );
                                    })}
                                </tr>
                            );
                        });
                    })}
                </tbody>
            </table>
        </div>
    );
};
